/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { ComponentRendererProps } from '../ComponentRenderer';
import { AudioRenderer } from '../common/Audio';
import { UserTextRenderer } from './Text';
import { Bubble } from '../Bubble';
import { UserBooleanRenderer } from './Boolean';
import { UserMCQRenderer } from './MCQ';
import { UserPRPTextComparisonRenderer } from './PRPTextComparison';
import { useEffect, type JSX } from 'react';
import { UserAudioSegmentRenderer } from './AudioSegmentation';
import { UserImageRenderer } from './Image';
import { UserVideoRenderer } from './Video';

const dockableComponents = ['TEXT'];
const autoNextComponents = ['VIDEO', 'IMAGE', 'AUDIO', 'AUDIO_SEGMENTATION', 'AUDIO_MARKER'];

export function UserComponentRenderer(props: ComponentRendererProps) {
  const { next, isCurrent } = props;
  const component = props.node.component;

  useEffect(() => {
    if (isCurrent && 'dtype' in component && autoNextComponents.includes(component.dtype)) {
      next();
    }
  }, [isCurrent, next, component]);

  if (component.ctype != 'USER') {
    return null;
  }

  if (props.isCurrent && dockableComponents.includes(component.dtype) !== props.isDocked) {
    return null;
  }

  let renderer: JSX.Element;
  switch (component.dtype) {
    case 'TEXT':
      renderer = <UserTextRenderer {...props} />;
      break;
    case 'VIDEO':
      renderer = <UserVideoRenderer />;
      break;
    case 'IMAGE':
      renderer = <UserImageRenderer />;
      break;
    case 'AUDIO':
    case 'AUDIO_MARKER':
      renderer = <AudioRenderer />;
      break;
    case 'AUDIO_SEGMENTATION':
      renderer = <UserAudioSegmentRenderer {...props} />;
      break;
    case 'BOOLEAN':
      renderer = <UserBooleanRenderer {...props} />;
      break;
    case 'MCQ':
      renderer = <UserMCQRenderer {...props} />;
      break;
    case 'PRP_TEXT_COMPARISON':
      renderer = <UserPRPTextComparisonRenderer {...props} />;
      break;
  }
  return <Bubble type="USER">{renderer}</Bubble>;
}
